import type { Chart, Plugin } from "chart.js";
import dayjs from "dayjs";

import type { PlannedOrderStatus } from "@/api/planningRun";
import { parseDate } from "@/utils/formatters";

import { FIRMED_COLOR, HISTORY_COLOR, PROJECTION_COLOR } from "./datasets";

export type PlanMarksOptions = {
  startDate?: string;
  /** Latest time fence of a cancelled order at the node. */
  fence: string | null;
  orders: { date: string; status: PlannedOrderStatus; highlight: boolean }[];
};

let hatch: CanvasPattern | null = null;
function hatchPattern(ctx: CanvasRenderingContext2D) {
  if (hatch) return hatch;
  const tile = document.createElement("canvas");
  tile.width = tile.height = 8;
  const g = tile.getContext("2d")!;
  g.strokeStyle = "rgba(100, 116, 139, 0.35)";
  g.beginPath();
  g.moveTo(0, 8);
  g.lineTo(8, 0);
  g.stroke();
  hatch = ctx.createPattern(tile, "repeat");
  return hatch;
}

const xOf = (chart: Chart, date: string) =>
  chart.scales.x.getPixelForValue(parseDate(date).getTime());

/** Draws the time fence as a hatched span from the start date through the fence,
 *  and one marker per order on the time axis: △ undecided (planned), ▲ accepted
 *  (firmed), ✕ discarded (cancelled); a highlighted order's marker is twice the size.
 *  Configure via options.plugins.planMarks. */
export const planMarksPlugin: Plugin<"line"> = {
  id: "planMarks",
  beforeDatasetsDraw(chart, _args, opts: PlanMarksOptions) {
    if (!opts.fence || !opts.startDate) return;
    const { ctx, chartArea: a } = chart;
    const x0 = Math.max(xOf(chart, opts.startDate), a.left);
    // The fence date is inclusive: the span ends where the next day starts.
    const x1 = Math.min(
      chart.scales.x.getPixelForValue(
        dayjs(opts.fence).add(1, "day").valueOf(),
      ),
      a.right,
    );
    if (x1 <= x0) return;
    ctx.save();
    ctx.fillStyle = hatchPattern(ctx) ?? "transparent";
    ctx.fillRect(x0, a.top, x1 - x0, a.bottom - a.top);
    ctx.restore();
  },
  afterDatasetsDraw(chart, _args, opts: PlanMarksOptions) {
    const { ctx, chartArea: a } = chart;
    const y = a.bottom - 7;
    ctx.save();
    ctx.lineWidth = 1.5;
    for (const order of opts.orders) {
      const x = xOf(chart, order.date);
      if (x < a.left || x > a.right) continue;
      const s = order.highlight ? 2 : 1;
      ctx.beginPath();
      if (order.status === "cancelled") {
        ctx.strokeStyle = HISTORY_COLOR;
        ctx.moveTo(x - 4 * s, y - 4 * s);
        ctx.lineTo(x + 4 * s, y + 4 * s);
        ctx.moveTo(x + 4 * s, y - 4 * s);
        ctx.lineTo(x - 4 * s, y + 4 * s);
        ctx.stroke();
        continue;
      }
      ctx.moveTo(x, y + 4 - 9 * s);
      ctx.lineTo(x + 5 * s, y + 4);
      ctx.lineTo(x - 5 * s, y + 4);
      ctx.closePath();
      if (order.status === "firmed") {
        ctx.fillStyle = FIRMED_COLOR;
        ctx.fill();
      } else {
        ctx.strokeStyle = PROJECTION_COLOR;
        ctx.stroke();
      }
    }
    ctx.restore();
  },
};
